import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useImpersonation from '../../hooks/useImpersonation';
import useAuth from '../../hooks/useAuth';
import { ktmThisMonth, ktmToday } from '../../utils/calendar';
import { FEE_STATUS_BADGE } from '../../utils/feeStatus';
import { getBeltStyle } from '../../utils/beltStyle';
import { BELT_ORDER } from '../../constants/belts';
import { openWhatsApp } from '../../utils/whatsapp';
import DualDate from '../../components/common/DualDate';
import { useConfirm } from '../../components/common/ConfirmModal';
import {
  MdSearch, MdPersonAdd, MdRemoveRedEye, MdBlock, MdCheckCircle,
  MdClose, MdSchool, MdCalendarToday, MdAdd, MdEdit, MdDelete,
  MdEmail, MdPhone, MdLocationOn, MdWarning, MdPerson, MdHistory,
  MdPayments, MdNotes, MdLockReset, MdRestore, MdArchive,
  MdDownload, MdTableChart, MdMoreVert, MdArrowForward, MdLink, MdContentCopy, MdExpandMore,
  MdPersonOff, MdVerifiedUser,
} from 'react-icons/md';
import { FaMedal, FaWhatsapp } from 'react-icons/fa';
import { downloadStudentListReport, downloadStudentProfile } from '../../services/reportExportService';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Pagination from '../../components/common/Pagination';
import { SkeletonRow } from '../../components/common/Skeleton';
import InviteModal from '../../components/common/InviteModal';
import AddStudentModal from '../../components/students/AddStudentModal';
import EditStudentModal from '../../components/students/EditStudentModal';
import PendingInvitationsPanel from '../../components/common/PendingInvitationsPanel';
import RecognizeModal from '../../components/common/RecognizeModal';
import StudentFinanceTab from '../../components/admin/students/StudentFinanceTab';
import AddChargeModal from '../../components/common/AddChargeModal';
import GiveDiscountModal from '../../components/common/GiveDiscountModal';
import AvatarUploader from '../../components/AvatarUploader';
import * as studentService from '../../services/studentService';
import { permanentDeleteStudent, restoreStudent } from '../../services/studentService';
import * as classService from '../../services/classService';
import { getStudentEnrollments, unenroll, enrollStudent } from '../../services/enrollmentService';
import { getStudentGamificationAdmin } from '../../services/gamificationService';
import { getDisplayTxId } from '../../utils/displayId';
import SuspendedAccessPanel from '../../components/common/SuspendedAccessPanel';
import { isClubSuspended, isClubCancelled } from '../../utils/clubError';
import { getTrialApplicationById } from '../../services/trialService';
import { TRIAL_ENABLED } from '../../config/features';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import * as feeSvc from '../../services/feeService';
import useEscapeKey from '../../hooks/useEscapeKey';
import useClubVocab from '../../hooks/useClubVocab';
import { hasBelts, hasClasses } from '../../utils/publicProfile';
import { calcAge } from '../../utils/age';
import MembershipCard from '../../components/admin/MembershipCard';

// ── Constants ────────────────────────────────────────────────────────────────
// Belt list now comes from constants/belts.js (BELT_ORDER) — the same canonical list used by
// belt-exam eligibility and the manual belt-correction editor, instead of a locally-duplicated
// array that used to disagree with both.

// ── Membership badge (gym/yoga/general) — plan name + status pill, Kathmandu dates ──────
const MEM_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtShortKtm = (iso) => {
  if (!iso) return '';
  const k = new Date(new Date(iso).getTime() + (5 * 60 + 45) * 60 * 1000);
  return `${k.getUTCDate()} ${MEM_MONTHS[k.getUTCMonth()]}`;
};
function MembershipBadge({ planName, expiresAt }) {
  if (!expiresAt) return <span className="text-[11px] font-medium text-subtle bg-nav-hover px-2 py-0.5 rounded-full">No plan</span>;
  const left = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000);
  let cls, label;
  if (left <= 0)      { cls = 'bg-red-500/15 text-red-500';         label = `Expired ${fmtShortKtm(expiresAt)}`; }
  else if (left <= 7) { cls = 'bg-amber-500/15 text-amber-500';     label = `Expires ${fmtShortKtm(expiresAt)}`; }
  else                { cls = 'bg-emerald-500/15 text-emerald-500'; label = `Active until ${fmtShortKtm(expiresAt)}`; }
  return (
    <div className="min-w-0">
      {planName && <p className="text-xs font-medium text-secondary truncate mb-0.5">{planName}</p>}
      <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{label}</span>
    </div>
  );
}

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const DAYS_SHORT = {
  Sun: 'Sun', Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat',
  Sunday: 'Sun', Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat',
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// ── Fee status badge config ───────────────────────────────────────────────────

const FEE_BADGE = {
  paid:      { label: 'Paid',      cls: FEE_STATUS_BADGE.paid },
  pending:   { label: 'Pending',   cls: FEE_STATUS_BADGE.pending },
  overdue:   { label: 'Overdue',   cls: FEE_STATUS_BADGE.overdue },
  no_record: { label: 'No record', cls: FEE_STATUS_BADGE.no_record },
};

function FeeBadge({ status, source }) {
  const cfg   = FEE_BADGE[status] || FEE_BADGE.no_record;
  const title = source === 'payment'     ? `${cfg.label} · via Payments page`
              : source === 'fee_payment' ? `${cfg.label} · via Fee system`
              : cfg.label;
  return (
    <span
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.cls}`}
      title={title}
    >
      {cfg.label}
    </span>
  );
}

// ── Attendance health badge config ───────────────────────────────────────────

const ATT_BADGE = {
  good: { label: 'Good',         cls: 'bg-green-100  text-green-700' },
  low:  { label: 'Low',          cls: 'bg-amber-500/15 text-amber-400 border border-amber-500/20' },
  none: { label: 'Not Attended', cls: 'bg-nav-hover  text-muted'  },
};

function AttBadge({ health }) {
  const cfg = ATT_BADGE[health];
  if (!cfg) return <span className="text-[10px] text-subtle">—</span>;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.cls}`}>{cfg.label}</span>;
}

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({ name, size = 'md', photoUrl }) {
  const sz = size === 'lg' ? 'w-14 h-14 text-xl' : 'w-9 h-9 text-sm';
  if (photoUrl) {
    return (
      <div className={`${sz} rounded-full overflow-hidden flex-shrink-0`}>
        <img src={photoUrl} alt={name || 'Avatar'} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={`${sz} bg-primary-light text-primary rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────

const fmtNPR = (n) => n != null ? `NPR ${Number(n).toLocaleString()}` : '—';

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-start gap-3 py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs text-subtle flex-shrink-0 w-28">{label}</span>
      <span className="text-sm text-secondary text-right">{value || '—'}</span>
    </div>
  );
}

function SectionHead({ icon: Icon, title, count, action }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="text-primary text-base" />
      <h3 className="font-semibold text-secondary text-sm">{title}</h3>
      {count != null && (
        <span className="text-xs bg-primary-light text-primary px-2 py-0.5 rounded-full">{count}</span>
      )}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}

function ProgressRing({ pct }) {
  const r = 28, stroke = 6;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(pct, 100) / 100) * circ;
  const color = pct >= 80 ? '#22c55e' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={72} height={72}>
      <circle cx={36} cy={36} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle cx={36} cy={36} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 36 36)" />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
        fontSize="14" fontWeight="bold" fill={color}>{pct}%</text>
    </svg>
  );
}

// ── Delete / Archive Confirmation Modal ───────────────────────────────────────

function DeleteConfirmModal({ student, onClose, onArchive, onPermanentDelete, archiving, deleting }) {
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  const [nameInput, setNameInput] = useState('');
  const studentName = student?.user?.name || '';
  const nameMatch   = nameInput.trim() === studentName.trim();

  useEscapeKey(onClose);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] px-4" onClick={onClose}>
      <div className="bg-card rounded-3xl shadow-2xl p-6 max-w-sm w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="overflow-y-auto flex-1 min-h-0">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <MdDelete className="text-red-600 text-xl" />
          </div>
          <div>
            <h3 className="font-bold text-secondary text-base leading-tight">{studentName}</h3>
            {student?.platformStudentId && (
              <p className="text-xs font-mono text-subtle">{student.platformStudentId}</p>
            )}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-4">
          <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
            <MdArchive className="text-base" /> Archive (Recommended)
          </p>
          <ul className="text-xs text-amber-700 space-y-0.5 mb-3">
            <li>• Hides {v.member.toLowerCase()} from the active list</li>
            <li>• Blocks their login immediately</li>
            <li>• Preserves all attendance and payment records</li>
            <li>• Frees their email for re-registration</li>
            <li className="text-green-700 font-semibold pt-1">✓ Reversible — can be restored anytime</li>
          </ul>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-muted hover:bg-nav-hover transition-colors">
              Cancel
            </button>
            <button onClick={onArchive} disabled={archiving || isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {archiving ? 'Archiving…' : 'Archive Student'}
            </button>
          </div>
        </div>

        <div className="border border-red-100 rounded-2xl p-4">
          <p className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1.5">
            <MdDelete className="text-base" /> Permanently Delete
          </p>
          <p className="text-xs text-red-600 mb-3">
            Removes their login and hides them from every list. Financial records and belt history are preserved — the student can be restored from the <strong>Archived</strong> tab if needed.
          </p>
          <p className="text-xs text-muted mb-1.5">
            Type <strong>{studentName}</strong> to confirm:
          </p>
          <input
            type="text" value={nameInput} onChange={e => setNameInput(e.target.value)}
            placeholder={`Type "${studentName}"`}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-red-500/30"
          />
          <button onClick={onPermanentDelete} disabled={!nameMatch || deleting || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {deleting ? 'Deleting…' : 'Permanently Delete'}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

// ── Change belt modal — a deliberate manual correction, not a routine field edit. Requires a
// reason (so "why is this student suddenly Blue" is always answerable) and never touches
// XP/badges/notifications — those belong to actually earning a belt via a belt exam. ──────
function ChangeBeltModal({ student, onClose, onSaved }) {
  useEscapeKey(onClose);
  const { isReadOnly } = useImpersonation();
  const currentGrade = student?.currentGrade || 'White';
  const [newBelt, setNewBelt] = useState(currentGrade);
  const [reason,  setReason]  = useState('');

  const mutation = useMutation({
    mutationFn: () => studentService.updateStudent(student._id, { currentGrade: newBelt, beltChangeReason: reason.trim() }),
    onSuccess:  () => { toast.success(`Belt updated to ${newBelt}`); onSaved(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update belt'),
  });

  const canSubmit = newBelt !== currentGrade && reason.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] px-4" onClick={onClose}>
      <div className="bg-card rounded-3xl shadow-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-secondary text-base">Change Belt</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-lg" />
          </button>
        </div>
        <p className="text-xs text-muted mb-4">
          A manual correction — recorded in Belt History as a correction, not an exam result.
          It does not award XP or badges and sends no congratulations notification.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Current belt</label>
            <p className="text-sm text-secondary px-3 py-2 bg-nav-hover rounded-xl">{currentGrade}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">New belt *</label>
            <select
              value={newBelt}
              onChange={e => setNewBelt(e.target.value)}
              className="w-full border border-divider rounded-xl px-3 py-2.5 text-sm bg-card text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {BELT_ORDER.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Reason *</label>
            <input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Transferred from another school at Blue belt"
              className="w-full border border-divider rounded-xl px-3 py-2.5 text-sm bg-card text-secondary placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 border border-divider rounded-xl text-sm text-muted hover:bg-nav-hover transition-colors">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="flex-1 py-2.5 bg-primary hover:opacity-90 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Student Detail Drawer ────────────────────────────────────────────────────

function StudentDrawer({ student, onClose, onAssignClass, onRecordPayment }) {
  const v = useClubVocab();
  const { isReadOnly } = useImpersonation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // discounts are admin-only (no trainers)
  const currentTheme = useTheme()?.currentTheme;
  const queryClient = useQueryClient();
  const navigate    = useNavigate();
  const { formatDate, formatMonth } = useCalendar();
  const [tab, setTab]                     = useState('info');
  const [confirmUnenroll, setConfirmUnenroll] = useState(null);
  const [confirmDelete,   setConfirmDelete]   = useState(false);
  const [notesValue,      setNotesValue]      = useState('');
  const [notesInit,       setNotesInit]       = useState(false);
  const [contactEmailValue, setContactEmailValue] = useState('');
  const [editOpen,        setEditOpen]        = useState(false);
  const [changeBeltOpen,  setChangeBeltOpen]  = useState(false);
  const [showRecognize,   setShowRecognize]   = useState(false);
  const [photoOverride,   setPhotoOverride]   = useState(null); // instant avatar update after upload
  const [giveAccessOpen,  setGiveAccessOpen]  = useState(false);
  const [accessEmail,     setAccessEmail]     = useState('');
  const [accessError,     setAccessError]     = useState('');
  const [showCharge,      setShowCharge]      = useState(false);
  const [showDiscount,    setShowDiscount]    = useState(false);

  const { data: profileData, isPending: loadingProfile } = useQuery({
    queryKey: ['student-full', student._id],
    queryFn:  () => studentService.getStudentById(student._id).then(r => r.data.student),
    staleTime: 30000,
  });

  const { data: gamData } = useQuery({
    queryKey: ['student-gam-admin', student._id],
    queryFn:  () => getStudentGamificationAdmin(student._id).then(r => r.data),
    staleTime: 120000,
    enabled:  tab === 'info',
  });

  const trialAppId = profileData?.trialApplicationId;
  const { data: trialApp } = useQuery({
    queryKey: ['trial-app-for-student', trialAppId],
    queryFn:  () => getTrialApplicationById(trialAppId).then(r => r.data.application),
    enabled:  !!trialAppId,
    staleTime: 300000,
  });

  if (profileData && !notesInit) {
    setNotesValue(profileData.notes || '');
    setContactEmailValue(profileData.contactEmail || '');
    setNotesInit(true);
  }

  const notesMutation = useMutation({
    mutationFn: (notes) => studentService.updateStudentNotes(student._id, notes),
    onSuccess:  () => toast.success('Notes saved'),
    onError:    () => toast.error('Failed to save notes'),
  });

  const contactEmailMutation = useMutation({
    mutationFn: (email) => studentService.updateStudentContactEmail(student._id, email),
    onSuccess:  () => { toast.success('Reminder email saved'); queryClient.invalidateQueries({ queryKey: ['student-full', student._id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to save reminder email'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ isActive }) => studentService.updateStudentStatus(student._id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(`${v.member} ${isActive ? 'activated' : 'deactivated'}`);
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student-full', student._id] });
    },
    onError: () => toast.error('Failed to update status'),
  });

  const archiveMutation = useMutation({
    mutationFn: () => studentService.deleteStudent(student._id),
    onSuccess: () => {
      toast.success(`${v.member} archived — they can be restored from the Archived tab`);
      queryClient.invalidateQueries({ queryKey: ['students'] });
      setConfirmDelete(false);
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to archive'),
  });

  // Stage B — class-placement review flag. 'kept'/'dismissed' resolve directly; 'moved' is called
  // AFTER the admin has already changed enrollments via the existing enroll/unenroll calls (see
  // ClassPlacementReviewModal below) — this mutation only snapshots placementAgeGroup + audits.
  const [showReviewModal, setShowReviewModal] = useState(false);
  const reviewMutation = useMutation({
    mutationFn: ({ action, addedClassIds, removedClassIds }) =>
      studentService.resolveClassPlacementReview(student._id, { action, addedClassIds, removedClassIds }),
    onSuccess: () => {
      toast.success('Class placement review resolved');
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student-full', student._id] });
      queryClient.invalidateQueries({ queryKey: ['student-enrollments-admin', student.user?._id] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not resolve the review'),
  });

  const hardDeleteMutation = useMutation({
    mutationFn: () => permanentDeleteStudent(student._id),
    onSuccess: () => {
      toast.success(`${v.member} permanently deleted`);
      queryClient.invalidateQueries({ queryKey: ['students'] });
      setConfirmDelete(false);
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to delete'),
  });

  const unenrollMutation = useMutation({
    mutationFn: unenroll,
    onSuccess: () => {
      toast.success(`${v.member} unenrolled`);
      queryClient.invalidateQueries({ queryKey: ['student-full', student._id] });
      setConfirmUnenroll(null);
    },
    onError: () => toast.error('Failed to unenroll'),
  });

  const resetMutation = useMutation({
    // :id is the StudentProfile _id (tenant-scoped, admin-only on the backend)
    mutationFn: () => studentService.sendStudentPasswordReset(student._id),
    onSuccess: () => toast.success('Password reset email sent.'),
    onError:   (e) => toast.error(e.response?.data?.message || 'Could not send reset email. Please try again.'),
  });

  // Give Access — upgrades a record-only student to a full login IN PLACE (same _id).
  const giveAccessMutation = useMutation({
    mutationFn: (email) => studentService.giveStudentAccess(student._id, email),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Access granted!');
      setGiveAccessOpen(false);
      setAccessEmail('');
      setAccessError('');
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student-full', student._id] });
    },
    onError: (e) => setAccessError(e.response?.data?.message || 'Could not grant access. Please try again.'),
  });

  const submitGiveAccess = (e) => {
    e.preventDefault();
    setAccessError('');
    const email = accessEmail.trim();
    if (!email) { setAccessError('Please enter an email address.'); return; }
    giveAccessMutation.mutate(email);
  };

  const [confirm, confirmModal] = useConfirm();
  const handleSendReset = async () => {
    if (resetMutation.isPending) return; // prevent double-send
    if (await confirm({ title: 'Send password reset?', message: `Send a password reset email to this ${v.member.toLowerCase()}?`, confirmLabel: 'Send email', destructive: false })) {
      resetMutation.mutate();
    }
  };

  const { club: tenantClub } = useTenant();
  const showBelts = hasBelts(tenantClub?.clubType);
  const showClasses = hasClasses(tenantClub?.clubType);
  const [copyingAction, setCopyingAction] = useState(null); // 'reminder'|'whatsapp'|'link'|'email'

  const handlePaymentAction = async (paymentId, type, payment) => {
    setCopyingAction(type);
    try {
      if (type === 'email') {
        await feeSvc.sendReminderEmail(paymentId);
        toast.success('Reminder email sent!');
        return;
      }

      const r    = await feeSvc.generatePaymentLink(paymentId);
      const link = r.data.link;
      const clubName = tenantClub?.name || 'Your Club';

      const guardian      = (profileData?.guardians || [])[0];
      const recipientName = guardian?.name || usr?.name || 'Guardian';
      const studentName   = usr?.name || 'your child';
      const period        = payment?.periodLabel || payment?.month || '';
      const amount        = (payment?.totalAmount || payment?.amount)?.toLocaleString();
      const msg = (
        `Namaste ${recipientName},\n\n` +
        `This is a reminder for ${studentName}'s monthly fee for ${period}.\n` +
        `Amount: NPR ${amount}.\n\n` +
        `You can pay using the secure link below:\n${link}\n\n` +
        `After payment, please upload the screenshot in the link for club approval.\n\n` +
        `Thank you,\n${clubName}`
      );

      if (type === 'link') {
        await navigator.clipboard.writeText(link);
        toast.success('Payment link copied!');
      } else if (type === 'reminder') {
        await navigator.clipboard.writeText(msg);
        toast.success('Reminder message copied!');
      } else if (type === 'whatsapp') {
        const phone = guardian?.phone || usr?.phone || null;
        if (phone && openWhatsApp(phone, msg)) {
          // opened
        } else {
          await navigator.clipboard.writeText(msg);
          toast.success('No usable WhatsApp number on file. Reminder copied instead.');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed');
    } finally {
      setCopyingAction(null);
    }
  };

  const p   = profileData || student;
  const usr = p?.user || {};
  const hasEmail = usr.email && !usr.email.includes('@noemail.club');
  const isActive = usr.isActive;
  const age      = calcAge(p?.dateOfBirth);
  // Effective first billable month = explicit feeStartDate, else the join month (mirrors the
  // backend billing-start guard). Shown read-only in the profile info section.
  const billingFrom = p?.feeStartDate || p?.joinDate;
  const billingFromMonth = billingFrom
    ? formatMonth(`${new Date(billingFrom).getUTCFullYear()}-${String(new Date(billingFrom).getUTCMonth() + 1).padStart(2, '0')}`)
    : null;
  const enrollments  = profileData?.enrollments || [];
  const attSummary   = profileData?.attendanceSummary;
  const payments     = profileData?.recentPayments || [];
  const beltHistory  = (profileData?.gradingHistory || []).slice().reverse();


  const TABS = [
    { key: 'info',     label: 'Profile'  },
    { key: 'classes',  label: 'Classes'  },
    { key: 'activity', label: 'Activity' },
    { key: 'finance',  label: 'Finance'  },
    { key: 'admin',    label: 'Admin'    },
  ];

  return (
    <>
      {confirmModal}
      <div className="fixed inset-0 z-50 flex">
        <div className="flex-1 bg-black/40" onClick={onClose} />
        <div className="w-full max-w-md bg-card h-full flex flex-col shadow-2xl relative">

          {/* Header */}
          <div className="flex-shrink-0 border-b border-gray-100">
            <div className="flex items-center justify-between px-5 py-3">
              <h2 className="font-bold text-secondary text-sm">{v.member} Profile</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditOpen(true)}
                  disabled={!profileData || isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : 'Edit Details'}
                  className="h-10 px-3 flex items-center gap-1.5 rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MdEdit className="text-lg" /> Edit
                </button>
                <button
                  onClick={() => downloadStudentProfile(student._id)}
                  title="Download Profile PDF"
                  className="w-10 h-10 flex items-center justify-center rounded-lg text-subtle hover:text-primary hover:bg-nav-hover transition-colors"
                >
                  <MdDownload className="text-xl" />
                </button>
                <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
                  <MdClose className="text-xl" />
                </button>
              </div>
            </div>

            <div className="px-5 pb-4 flex items-center gap-4">
              <AvatarUploader
                currentPhotoUrl={photoOverride || p?.profilePhoto || null}
                studentName={usr.name}
                uploadUrl={`/api/students/${student._id}/photo`}
                size={64}
                onUploaded={(url) => {
                  setPhotoOverride(url); // instant
                  queryClient.invalidateQueries({ queryKey: ['student-full', student._id] });
                  queryClient.invalidateQueries({ queryKey: ['students'] });
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-secondary truncate">{usr.name}</p>
                {hasEmail && <p className="text-xs text-subtle truncate">{usr.email}</p>}
                {(usr.phone || p?.phone) && <p className="text-xs text-subtle">{usr.phone || p?.phone}</p>}
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {showBelts && (
                  <span className="inline-flex items-center gap-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getBeltStyle(p?.currentGrade)}`}>
                      {p?.currentGrade || 'White'} Belt
                    </span>
                    {isAdmin && (
                      <button
                        onClick={() => setChangeBeltOpen(true)}
                        disabled={!profileData || isReadOnly}
                        title={isReadOnly ? 'Disabled: read-only support mode' : 'Change belt'}
                        className="w-5 h-5 flex items-center justify-center rounded-full text-subtle hover:text-primary hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MdEdit className="text-xs" />
                      </button>
                    )}
                    {p?.beltNeedsReview && (
                      <MdWarning title="Needs belt review — signed up via self-signup with no belt question asked" className="text-amber-500 text-sm" />
                    )}
                  </span>
                  )}
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                  {student.feeStatus && <FeeBadge status={student.feeStatus} source={student.feeSource} />}
                  {student.attendanceHealth && <AttBadge health={student.attendanceHealth} />}
                </div>
              </div>
            </div>

            <div className="flex border-t border-gray-100">
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2
                    ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-subtle hover:text-muted'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {loadingProfile && (
              <div className="p-5 space-y-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
              </div>
            )}

            {!loadingProfile && tab === 'info' && (
              <div className="p-5 space-y-5">
                {/* Gamification summary */}
                {gamData?.gamification && (() => {
                  const g  = gamData.gamification;
                  const gr = g.gradingReadiness;
                  const grColor = gr?.status === 'ready' ? 'text-green-600' : gr?.status === 'getting_close' ? 'text-orange-500' : 'text-subtle';
                  return (
                    <div className="bg-gray-50 rounded-2xl px-4 py-3 flex flex-wrap gap-4">
                      <div className="text-center">
                        <p className="text-lg font-black text-secondary leading-none">{g.level}</p>
                        <p className="text-[10px] text-subtle mt-0.5">{g.levelName}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-black text-orange-500 leading-none">🔥 {g.streak}</p>
                        <p className="text-[10px] text-subtle mt-0.5">streak</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-black text-yellow-500 leading-none">{(g.xpPoints || 0).toLocaleString()}</p>
                        <p className="text-[10px] text-subtle mt-0.5">XP</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-black text-purple-600 leading-none">{g.badgeCount}</p>
                        <p className="text-[10px] text-subtle mt-0.5">badges</p>
                      </div>
                      {gr && gr.status !== 'not_ready' && (
                        <div className={`text-center ${grColor}`}>
                          <p className="text-sm font-black leading-none">
                            {gr.status === 'ready' ? '✅' : gr.status === 'getting_close' ? '🔥' : ''}
                          </p>
                          <p className={`text-[10px] font-semibold mt-0.5 ${grColor}`}>
                            {gr.status === 'ready' ? 'Grading Ready' : 'Getting Close'}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div>
                  <SectionHead icon={MdPerson} title="Personal Details" />
                  <InfoRow label="Full name"     value={p?.fullName || usr.name} />
                  {(p?.txId || usr?.txId) && (
                    <div className="flex justify-between items-center gap-3 py-2 border-b border-gray-50">
                      <span className="text-xs text-subtle flex-shrink-0 w-28">TrainAxis ID</span>
                      <button
                        onClick={() => { const id = p?.txId || usr?.txId; navigator.clipboard.writeText(id).catch(() => {}); toast.success('TrainAxis ID copied!', { duration: 1200 }); }}
                        className="font-mono text-xs bg-gray-100 text-muted font-semibold px-2 py-0.5 rounded-md hover:bg-nav-hover transition-colors"
                      >
                        {p?.txId || usr?.txId}
                      </button>
                    </div>
                  )}
                  {p?.platformStudentId && (
                    <div className="flex justify-between items-center gap-3 py-2 border-b border-gray-50">
                      <span className="text-xs text-subtle flex-shrink-0 w-28">Member ID</span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(p.platformStudentId).catch(() => {}); toast.success('Member ID copied!', { duration: 1200 }); }}
                        className="font-mono text-xs bg-gray-100 text-muted font-semibold px-2 py-0.5 rounded-md hover:bg-nav-hover transition-colors"
                      >
                        {p.platformStudentId}
                      </button>
                    </div>
                  )}
                  {p?.studentId && <InfoRow label="Club ID" value={<span className="font-mono text-xs">{p.studentId}</span>} />}
                  <InfoRow label="Date of birth" value={p?.dateOfBirth
                    ? <span className="inline-flex items-center gap-1.5"><DualDate date={p.dateOfBirth} />{age != null ? ` (${age} yrs)` : ''}</span>
                    : <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-nav-hover text-muted">No DOB</span>} />
                  {showClasses && p?.dateOfBirth && (p?.ageGroup || p?.classPlacementReviewRequired) && (
                    <InfoRow label="Age group" value={
                      <span className="inline-flex items-center flex-wrap gap-1.5">
                        {p?.ageGroup && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-nav-hover text-secondary">
                            {p.ageGroup}
                          </span>
                        )}
                        {p?.classPlacementReviewRequired && (
                          <button type="button" onClick={() => setTab('admin')}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20 hover:opacity-90">
                            <MdWarning className="text-xs" /> Placed as {p.placementAgeGroup} — review →
                          </button>
                        )}
                      </span>
                    } />
                  )}
                  <InfoRow label="Gender"      value={p?.gender?.replace(/_/g, ' ')} />
                  <InfoRow label="Discipline"  value={p?.discipline} />
                  <InfoRow label="Joined"      value={<DualDate date={p?.joinDate} />} />
                  <InfoRow label="Billing from" value={billingFromMonth} />
                </div>

                {(p?.address?.street || p?.address?.city || p?.address?.district) && (
                  <div>
                    <SectionHead icon={MdLocationOn} title="Address" />
                    <p className="text-sm text-secondary">
                      {[p.address.street, p.address.city, p.address.district, p.address.province].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}

                {(p?.emergencyContact?.name || p?.emergencyContact?.phone) && (
                  <div>
                    <SectionHead icon={MdPhone} title="Emergency Contact" />
                    <InfoRow label="Name"         value={p.emergencyContact.name} />
                    <InfoRow label="Relationship" value={p.emergencyContact.relationship} />
                    <InfoRow label="Phone"        value={p.emergencyContact.phone} />
                  </div>
                )}

                {p?.guardians?.length > 0 && (
                  <div>
                    <SectionHead icon={MdPerson} title={`Guardian${p.guardians.length > 1 ? 's' : ''}`} count={p.guardians.length} />
                    <div className="space-y-3">
                      {p.guardians.map((g, i) => (
                        <div key={i} className="bg-gray-50 rounded-xl p-3">
                          <p className="text-sm font-semibold text-secondary">{g.name}</p>
                          <p className="text-xs text-muted mt-0.5">{g.relationship}{g.phone ? ` · ${g.phone}` : ''}</p>
                          {g.isEmergencyContact && (
                            <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-semibold mt-1 inline-block">Emergency Contact</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {p?.medicalInfo ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <MdWarning className="text-yellow-500" />
                      <span className="text-xs font-semibold text-yellow-700">Medical Information</span>
                    </div>
                    <p className="text-sm text-yellow-800">{p.medicalInfo}</p>
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-200 rounded-xl p-3 text-center">
                    <p className="text-xs text-subtle">No medical conditions on file</p>
                  </div>
                )}
              </div>
            )}

            {!loadingProfile && tab === 'classes' && (
              <div className="p-5 space-y-4">
                {p?.classAssignmentPending?.classId && (
                  <div className="bg-amber-500/15 border border-amber-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <MdWarning className="text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-secondary">Needs class assignment</p>
                        <p className="text-xs text-muted mt-0.5">
                          Signed up wanting <strong className="text-secondary">{p.classAssignmentPending.className}</strong>, but
                          it was full at the time — no class was assigned. Add one below.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <SectionHead icon={MdSchool} title="Class Enrollments" count={enrollments.length} />
                  <button onClick={() => onAssignClass(student)}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className="flex items-center gap-1 text-xs text-primary font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed">
                    <MdAdd /> Add to Class
                  </button>
                </div>
                {enrollments.length === 0 ? (
                  <div className="border border-dashed border-gray-200 rounded-xl py-10 text-center">
                    <MdCalendarToday className="text-3xl text-subtle mx-auto mb-2" />
                    <p className="text-sm text-subtle">Not enrolled in any class</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {enrollments.map(e => {
                      const cls = e.classId;
                      if (!cls) return null;
                      const trainerName = cls.trainer?.user?.name || cls.trainer?.fullName;
                      return (
                        <div key={e._id} className="border border-gray-100 rounded-xl p-3 hover:border-primary/30 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-secondary">{cls.name}</p>
                              <p className="text-xs text-subtle mt-0.5">
                                {cls.days?.map(d => DAYS_SHORT[d] || d).join(', ')}
                                {cls.startTime && ` · ${fmtTime(cls.startTime)}`}
                              </p>
                              {trainerName && <p className="text-xs text-subtle">Trainer: {trainerName}</p>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-muted'}`}>
                                {e.status}
                              </span>
                              <button onClick={() => setConfirmUnenroll(e)} disabled={isReadOnly}
                                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                                className="text-xs text-red-400 hover:text-red-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed">Unenroll</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {!loadingProfile && tab === 'activity' && (
              <div className="p-5 space-y-5">
                <div>
                  <SectionHead icon={MdCheckCircle} title="Attendance — This Month" />
                  {attSummary ? (
                    <div className="flex items-center gap-5">
                      <ProgressRing pct={attSummary.percentage} />
                      <div className="flex-1 space-y-1.5">
                        {[
                          { label: 'Present', val: attSummary.thisMonth.present, color: 'text-green-600' },
                          { label: 'Late',    val: attSummary.thisMonth.late,    color: 'text-amber-500' },
                          { label: 'Absent',  val: attSummary.thisMonth.absent,  color: 'text-red-500'   },
                        ].map(({ label, val, color }) => (
                          <div key={label} className="flex items-center justify-between">
                            <span className="text-xs text-muted">{label}</span>
                            <span className={`text-sm font-bold ${color}`}>{val}</span>
                          </div>
                        ))}
                        {attSummary.lastAttended && (
                          <p className="text-xs text-subtle pt-1">Last attended: <DualDate date={attSummary.lastAttended} /></p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-subtle">No attendance records this month</p>
                  )}
                </div>

                <div className={showBelts ? '' : 'hidden'}>
                  {p?.beltNeedsReview && (
                    <div className="bg-amber-500/15 border border-amber-500/20 rounded-xl p-4 mb-3">
                      <div className="flex items-start gap-2">
                        <MdWarning className="text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-secondary">Needs belt review</p>
                          <p className="text-xs text-muted mt-0.5">
                            This student signed up without being asked their belt — defaulted to White.
                            Use the pencil next to the belt badge above to set their real rank.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <SectionHead icon={FaMedal} title="Belt History" count={beltHistory.length} />
                  {beltHistory.length === 0 ? (
                    <div className="border border-dashed border-gray-200 rounded-xl py-6 text-center">
                      <p className="text-xs text-subtle">No grading records yet</p>
                    </div>
                  ) : (
                    <div className="relative pl-4 space-y-3">
                      <div className="absolute left-1.5 top-0 bottom-0 w-px bg-gray-200" />
                      {beltHistory.map((g, i) => {
                        const isManual = g.source === 'manual';
                        const dotColor = isManual ? 'bg-blue-400' : g.passed ? 'bg-green-500' : 'bg-red-400';
                        return (
                        <div key={i} className="relative flex items-start gap-3">
                          <div className={`absolute -left-[11px] w-3 h-3 rounded-full border-2 border-white mt-0.5 ${dotColor}`} />
                          <div className="bg-gray-50 rounded-xl p-3 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-secondary">{g.fromGrade} → {g.toGrade}</p>
                              {isManual ? (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                  Manual correction
                                </span>
                              ) : (
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${g.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                  {g.passed ? 'Passed' : 'Failed'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-subtle mt-0.5"><DualDate date={g.date} />{g.examiner ? ` · ${g.examiner}` : ''}</p>
                            {isManual && g.notes && (
                              <p className="text-xs text-subtle mt-0.5 italic">"{g.notes}"</p>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!loadingProfile && tab === 'finance' && (
              <div>
                {/* Membership — gym / yoga / general clubs only (never taekwondo) */}
                {tenantClub?.clubType !== 'taekwondo' && (usr._id || profileData?.user?._id) && (
                  <div className="p-5 pb-0">
                    <MembershipCard
                      studentId={usr._id || profileData?.user?._id}
                      currentPlanId={p?.currentPlanId?._id || p?.currentPlanId}
                      currentPlanName={p?.currentPlanId?.name}
                      membershipExpiresAt={p?.membershipExpiresAt}
                    />
                  </div>
                )}
                <StudentFinanceTab studentId={student?.user?._id || profileData?.user?._id} />
              </div>
            )}

            {!loadingProfile && tab === 'admin' && (
              <div className="p-5 space-y-5">
                {/* Class-placement review flag (Stage B) — surfaced first in this tab so it's
                    seen without scrolling. Only rendered when actually flagged. */}
                {showClasses && p?.classPlacementReviewRequired && (
                  <div className="bg-amber-500/15 border border-amber-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <MdWarning className="text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-secondary">Age group changed — class review required</p>
                        <p className="text-xs text-muted mt-0.5">
                          Placed as <strong className="text-secondary">{p.placementAgeGroup || '—'}</strong>, now <strong className="text-secondary">{p.ageGroup}</strong>.
                          {' '}{enrollments.length > 0
                            ? `Currently in: ${enrollments.map(e => e.classId?.name).filter(Boolean).join(', ')}.`
                            : 'Not currently enrolled in any class.'}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <button
                            onClick={() => reviewMutation.mutate({ action: 'kept' })}
                            disabled={reviewMutation.isPending || isReadOnly}
                            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-card border border-divider text-secondary hover:bg-nav-hover disabled:opacity-50 disabled:cursor-not-allowed">
                            Keep current class{enrollments.length === 1 ? '' : 'es'}
                          </button>
                          <button
                            onClick={() => setShowReviewModal(true)}
                            disabled={reviewMutation.isPending || isReadOnly}
                            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
                            Change class
                          </button>
                          <button
                            onClick={() => reviewMutation.mutate({ action: 'dismissed' })}
                            disabled={reviewMutation.isPending || isReadOnly}
                            className="px-3 py-1.5 rounded-full text-xs font-medium text-muted hover:text-secondary hover:underline disabled:opacity-50 disabled:cursor-not-allowed">
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current month fee status — merged from FeePayment + legacy Payment */}
                <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-secondary">This Month's Fee</p>
                    <p className="text-[10px] text-subtle mt-0.5">{new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <FeeBadge status={p.feeStatus || 'no_record'} source={p.feeSource} />
                    {p.feeAmount != null && (
                      <span className="text-xs font-semibold text-secondary">{fmtNPR(p.feeAmount)}</span>
                    )}
                    <button
                      onClick={() => navigate(`/admin/payments?search=${encodeURIComponent(usr.name || '')}`)}
                      className="text-[10px] text-primary hover:underline font-semibold flex items-center gap-0.5">
                      Payments <MdArrowForward className="text-xs" />
                    </button>
                  </div>
                </div>

                {/* Primary billing actions — kept at the top of the tab so they're reachable
                    without scrolling. Side-by-side on wider drawers, stacked on narrow. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    variant="inverted"
                    fullWidth
                    // Trainers don't see Give Discount — let Add Charge span both columns instead
                    // of sitting half-width next to a gap.
                    className={`active:scale-[0.98] ${isAdmin ? '' : 'sm:col-span-2'}`}
                    onClick={() => setShowCharge(true)}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  >
                    Add Charge
                  </Button>
                  {/* Discounts are admin-only — hidden entirely for trainers. */}
                  {isAdmin && (
                    <Button
                      variant="inverted"
                      fullWidth
                      className="active:scale-[0.98]"
                      onClick={() => setShowDiscount(true)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    >
                      Give Discount
                    </Button>
                  )}
                </div>

                {/* Recent payments */}
                <div>
                  <SectionHead icon={MdPayments} title="Recent Payments" />
                  {payments.length === 0 ? (
                    <div className="border border-dashed border-gray-200 rounded-xl py-6 text-center">
                      <p className="text-xs text-subtle">No payment records found</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {payments.map(pay => (
                        <div key={pay._id} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5">
                          <div>
                            <p className="text-sm font-semibold text-secondary">{fmtNPR(pay.amount)}</p>
                            <p className="text-xs text-subtle">{pay.month} · {pay.method || 'cash'}</p>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            FEE_STATUS_BADGE[pay.status] || FEE_STATUS_BADGE.overdue
                          }`}>{pay.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Trial history */}
                {trialApp && (
                  <div>
                    <SectionHead icon={MdHistory} title="Trial History" />
                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-indigo-400">Applied</span>
                        <span className="text-xs font-medium text-indigo-700">
                          {new Date(trialApp.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-indigo-400">Trial Date</span>
                        <span className="text-xs font-medium text-indigo-700">
                          {trialApp.preferredTrialDate ? new Date(trialApp.preferredTrialDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-indigo-400">Converted</span>
                        <span className="text-xs font-medium text-indigo-700">
                          {trialApp.convertedAt ? new Date(trialApp.convertedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </span>
                      </div>
                      {trialApp.assignedTrainer?.name && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-indigo-400">Trial Trainer</span>
                          <span className="text-xs font-medium text-indigo-700">{trialApp.assignedTrainer.name}</span>
                        </div>
                      )}
                      <p className="text-[10px] text-indigo-300 pt-1 border-t border-indigo-100">Read-only — trial application history</p>
                    </div>
                  </div>
                )}

                {/* Reminder email — members without login access (record-only) still get reminders/receipts */}
                {!hasEmail && (
                  <div>
                    <SectionHead icon={MdEmail} title="Reminder Email" />
                    <input
                      type="email"
                      value={contactEmailValue}
                      onChange={e => setContactEmailValue(e.target.value)}
                      onBlur={() => { const val = contactEmailValue.trim().toLowerCase(); if (val !== (p?.contactEmail || '')) contactEmailMutation.mutate(val); }}
                      disabled={isReadOnly}
                      placeholder="email@example.com"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary resize-none transition-colors disabled:opacity-50"
                    />
                    <p className="text-xs text-subtle mt-1">They&apos;ll get fee reminders and receipts here — but no login access. Auto-saves when you click away.</p>
                  </div>
                )}

                {/* Admin notes */}
                <div>
                  <SectionHead icon={MdNotes} title="Admin Notes" />
                  <textarea
                    value={notesValue} onChange={e => setNotesValue(e.target.value)}
                    onBlur={() => notesMutation.mutate(notesValue)}
                    rows={4} placeholder="Private notes visible only to admins…"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary resize-none transition-colors"
                  />
                  <p className="text-xs text-subtle mt-1">Auto-saves when you click away</p>
                </div>

                {/* Actions */}
                <div>
                  <SectionHead icon={MdEdit} title="Actions" />
                  <div className="space-y-2">
                    <button
                      onClick={() => onRecordPayment?.(student)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="w-full flex items-center gap-2 px-4 py-2.5 border border-green-200 rounded-xl text-sm text-green-700 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MdPayments className="text-green-500 text-lg" /> Record Payment
                    </button>
                    <button
                      onClick={() => setShowRecognize(true)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="w-full flex items-center gap-2 px-4 py-2.5 border border-purple-200 rounded-xl text-sm text-purple-700 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      🎖️ Recognize {v.member}
                    </button>
                    {(() => {
                      const thisMonth = ktmThisMonth(); // Kathmandu month, not UTC
                      const due = payments.find(pay =>
                        pay.month === thisMonth && ['pending', 'overdue', 'partial'].includes(pay.status)
                      );
                      if (!due) return null;
                      const guardian = (p?.guardians || [])[0];
                      const hasPhone = !!(guardian?.phone || usr?.phone);
                      return (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => handlePaymentAction(due._id, 'reminder', due)}
                            disabled={!!copyingAction}
                            className="flex flex-col items-center gap-1 px-2 py-2.5 border border-gray-200 rounded-xl text-xs text-secondary hover:bg-nav-hover disabled:opacity-50 transition-colors"
                          >
                            {copyingAction === 'reminder'
                              ? <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                              : <MdContentCopy className="text-base text-muted" />}
                            Copy Reminder
                          </button>
                          <button
                            onClick={() => handlePaymentAction(due._id, 'whatsapp', due)}
                            disabled={!!copyingAction}
                            title={hasPhone ? 'Open WhatsApp' : 'No phone on file — will copy reminder'}
                            className="flex flex-col items-center gap-1 px-2 py-2.5 border border-green-200 rounded-xl text-xs text-green-700 hover:bg-nav-hover disabled:opacity-50 transition-colors"
                          >
                            {copyingAction === 'whatsapp'
                              ? <span className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                              : <FaWhatsapp className="text-base text-green-600" />}
                            WhatsApp
                          </button>
                          <button
                            onClick={() => handlePaymentAction(due._id, 'link', due)}
                            disabled={!!copyingAction}
                            className="flex flex-col items-center gap-1 px-2 py-2.5 border border-indigo-200 rounded-xl text-xs text-indigo-700 hover:bg-nav-hover disabled:opacity-50 transition-colors"
                          >
                            {copyingAction === 'link'
                              ? <span className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                              : <MdLink className="text-base text-indigo-500" />}
                            Copy Link
                          </button>
                          <button
                            onClick={() => handlePaymentAction(due._id, 'email', due)}
                            disabled={!!copyingAction || isReadOnly}
                            title={isReadOnly ? 'Disabled: read-only support mode' : 'Send reminder email to guardian or student'}
                            className="flex flex-col items-center gap-1 px-2 py-2.5 border border-orange-200 rounded-xl text-xs text-orange-700 hover:bg-nav-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {copyingAction === 'email'
                              ? <span className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                              : <MdEmail className="text-base text-orange-500" />}
                            Send Email
                          </button>
                        </div>
                      );
                    })()}
                    <button
                      onClick={() => navigate(`/admin/payments?search=${encodeURIComponent(usr.name || '')}`)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-secondary hover:bg-nav-hover transition-colors"
                    >
                      <MdPayments className="text-subtle text-lg" /> View All Payments
                    </button>
                    <button
                      onClick={() => downloadStudentProfile(student._id)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-secondary hover:bg-nav-hover transition-colors"
                    >
                      <MdDownload className="text-primary text-lg" /> Download Profile PDF
                    </button>
                    {/* Platform Access — record-only students can be upgraded in place */}
                    <div className="border border-divider rounded-xl p-3.5">
                      <div className="flex items-center gap-2 mb-1">
                        <MdVerifiedUser className={`text-lg ${usr.isRecordOnly ? 'text-subtle' : 'text-green-600'}`} />
                        <p className="text-sm font-semibold text-secondary">Platform Access</p>
                      </div>
                      {usr.isRecordOnly ? (
                        <>
                          <p className="text-xs text-muted mb-3">This {v.member.toLowerCase()} is tracked for attendance and payments but cannot sign in.</p>
                          <button
                            onClick={() => { setAccessError(''); setAccessEmail(''); setGiveAccessOpen(true); }}
                            disabled={isReadOnly}
                            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                            className={`${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full font-medium px-4 py-2 text-sm hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            Give Access
                          </button>
                        </>
                      ) : (
                        <p className="text-xs text-muted flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                          Active{hasEmail && <> — <span className="text-secondary">{usr.email}</span></>}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={handleSendReset}
                      disabled={!hasEmail || resetMutation.isPending || isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : 'Send password reset email'}
                      className="w-full flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-secondary hover:bg-nav-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <MdLockReset className="text-blue-500 text-lg" />
                      {resetMutation.isPending ? 'Sending…' : 'Send Password Reset Email'}
                      {!hasEmail && <span className="text-xs text-subtle ml-auto">No email on file</span>}
                    </button>
                    <button
                      onClick={() => statusMutation.mutate({ isActive: !isActive })}
                      disabled={statusMutation.isPending || isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                        ${isActive ? 'border-red-200 text-red-600 hover:bg-nav-hover' : 'border-green-200 text-green-600 hover:bg-nav-hover'}`}
                    >
                      {isActive ? <MdBlock className="text-lg" /> : <MdCheckCircle className="text-lg" />}
                      {statusMutation.isPending ? 'Updating…' : isActive ? 'Deactivate Account' : 'Activate Account'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(true)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="w-full flex items-center gap-2 px-4 py-2.5 border border-red-200 rounded-xl text-sm text-red-600 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MdDelete className="text-lg" /> Archive or Delete Student
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Unenroll confirm */}
          {confirmUnenroll && (
            <div className="absolute inset-0 bg-black/40 z-10 flex items-center justify-center p-6">
              <div className="bg-card rounded-2xl shadow-2xl p-6 w-full max-w-xs">
                <h3 className="font-bold text-secondary mb-2">Unenroll {v.member}?</h3>
                <p className="text-sm text-muted mb-5">
                  Remove <strong>{usr.name}</strong> from <strong>{confirmUnenroll.classId?.name}</strong>?
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setConfirmUnenroll(null)} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-muted hover:bg-nav-hover">Cancel</button>
                  <button
                    onClick={() => unenrollMutation.mutate(confirmUnenroll._id)}
                    disabled={unenrollMutation.isPending || isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className="flex-1 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed">
                    {unenrollMutation.isPending ? 'Removing…' : 'Unenroll'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <DeleteConfirmModal
          student={{ ...p, user: usr }}
          onClose={() => setConfirmDelete(false)}
          onArchive={() => archiveMutation.mutate()}
          onPermanentDelete={() => hardDeleteMutation.mutate()}
          archiving={archiveMutation.isPending}
          deleting={hardDeleteMutation.isPending}
        />
      )}

      {editOpen && profileData && (
        <EditStudentModal
          student={profileData}
          onClose={() => setEditOpen(false)}
        />
      )}

      {changeBeltOpen && profileData && (
        <ChangeBeltModal
          student={profileData}
          onClose={() => setChangeBeltOpen(false)}
          onSaved={() => { setChangeBeltOpen(false); queryClient.invalidateQueries({ queryKey: ['student-full', student._id] }); queryClient.invalidateQueries({ queryKey: ['students'] }); }}
        />
      )}

      {showRecognize && (
        <RecognizeModal
          studentProfileId={student._id}
          studentName={usr.name || student.user?.name}
          onClose={() => setShowRecognize(false)}
        />
      )}

      {showCharge && (
        <AddChargeModal
          studentId={student?.user?._id || profileData?.user?._id}
          onClose={() => setShowCharge(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['students'] });
            queryClient.invalidateQueries({ queryKey: ['studentFinance', student?.user?._id || profileData?.user?._id] });
          }}
        />
      )}

      {showDiscount && isAdmin && (
        <GiveDiscountModal
          studentId={student?.user?._id || profileData?.user?._id}
          onClose={() => setShowDiscount(false)}
          onCreated={() => {
            const sid = student?.user?._id || profileData?.user?._id;
            queryClient.invalidateQueries({ queryKey: ['students'] });
            queryClient.invalidateQueries({ queryKey: ['studentFinance', sid] });
            queryClient.invalidateQueries({ queryKey: ['student-unpaid-months', sid] });
            queryClient.invalidateQueries({ queryKey: ['fee-payments'] });
          }}
        />
      )}

      {showReviewModal && (
        <ClassPlacementReviewModal
          student={student}
          onClose={() => setShowReviewModal(false)}
        />
      )}

      {giveAccessOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
          onClick={(e) => e.target === e.currentTarget && setGiveAccessOpen(false)}>
          <form onSubmit={submitGiveAccess} className="bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
              <h3 className="font-bold text-secondary">Give Platform Access</h3>
              <button type="button" onClick={() => setGiveAccessOpen(false)} className="text-muted hover:text-secondary"><MdClose size={20} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Email <span className="text-red-400">*</span></label>
                <input
                  type="email"
                  autoFocus
                  value={accessEmail}
                  onChange={(e) => { setAccessEmail(e.target.value); if (accessError) setAccessError(''); }}
                  placeholder="student@email.com"
                  className="w-full px-3 py-2.5 rounded-full text-sm bg-card border border-divider text-secondary placeholder:text-subtle focus:outline-none focus:border-secondary transition-colors"
                />
              </div>
              <p className="text-xs text-muted">The {v.member.toLowerCase()} will receive a temporary password by email and must change it on first sign-in.</p>
              {accessError && <p className="text-xs text-red-500">{accessError}</p>}
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button
                type="button"
                onClick={() => setGiveAccessOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-full border border-divider text-sm font-medium text-secondary hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={giveAccessMutation.isPending || isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className={`flex-1 px-4 py-2.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {giveAccessMutation.isPending ? 'Sending…' : 'Send Access'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

// ── Assign to Class Modal ────────────────────────────────────────────────────

function AssignClassModal({ student, onClose }) {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);

  const { data: classData, isLoading } = useQuery({
    queryKey: ['classes-all'],
    queryFn:  () => classService.getClasses({ limit: 100 }).then(r => r.data),
    staleTime: 60000,
  });
  const { data: enrollData } = useQuery({
    queryKey: ['student-enrollments-admin', student?.user?._id],
    queryFn:  () => getStudentEnrollments(student?.user?._id).then(r => r.data),
    enabled:  !!student?.user?._id,
  });

  const classes     = classData?.classes || [];
  const enrolledIds = new Set((enrollData?.enrollments || []).map(e => e.classId?._id).filter(Boolean));
  const available   = classes.filter(c => !enrolledIds.has(c._id));

  const toggle = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => Promise.all(selectedIds.map(classId => enrollStudent({ studentId: student.user._id, classId }))),
    onSuccess: () => {
      toast.success(`Enrolled in ${selectedIds.length} class${selectedIds.length !== 1 ? 'es' : ''}`);
      queryClient.invalidateQueries({ queryKey: ['student-enrollments-admin', student.user._id] });
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Enrollment failed'),
  });

  useEscapeKey(onClose);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-secondary">Assign to Class</h2>
            <p className="text-xs text-subtle mt-0.5">{student.user?.name}</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {isLoading && [1, 2, 3].map(i => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-xl" />)}
          {!isLoading && !available.length && (
            <div className="text-center py-8 text-subtle">
              <MdSchool className="text-3xl mx-auto mb-2 opacity-30" />
              <p className="text-sm">No available classes</p>
            </div>
          )}
          {available.map(cls => {
            const checked = selectedIds.includes(cls._id);
            return (
              <button key={cls._id} type="button" onClick={() => toggle(cls._id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all
                  ${checked ? 'border-secondary bg-nav-hover' : 'border-divider hover:border-secondary'}`}>
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'bg-secondary border-secondary' : 'border-divider'}`}>
                  {checked && <MdCheckCircle className="text-card text-xs" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary truncate">{cls.name}</p>
                  <p className="text-xs text-subtle">{cls.days?.map(d => DAYS_SHORT[d] || d).join(', ')}{cls.startTime && ` · ${fmtTime(cls.startTime)}`}</p>
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-4 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-muted hover:bg-nav-hover">Cancel</button>
          <button onClick={() => mutate()} disabled={isPending || !selectedIds.length || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className={`flex-1 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed`}>
            {isPending ? 'Enrolling…' : `Enroll (${selectedIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Class Placement Review — "Change class" (Stage B) ────────────────────────
// The flexible version of AssignClassModal: lets the admin both drop current classes AND add
// new ones in one go (a student changing age group may need to drop one class and add two).
// Enrollment changes go through the EXISTING enroll/unenroll endpoints — no new enrollment
// plumbing — then resolveClassPlacementReview snapshots placementAgeGroup + audits what changed.
function ClassPlacementReviewModal({ student, onClose, onResolved }) {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  const queryClient = useQueryClient();
  const [toRemove, setToRemove] = useState(new Set()); // classIds currently enrolled, marked to drop
  const [toAdd, setToAdd]       = useState(new Set()); // classIds not enrolled, marked to add

  const { data: classData, isLoading: loadingClasses } = useQuery({
    queryKey: ['classes-all'],
    queryFn:  () => classService.getClasses({ limit: 100 }).then(r => r.data),
    staleTime: 60000,
  });
  const { data: enrollData, isLoading: loadingEnrollments } = useQuery({
    queryKey: ['student-enrollments-admin', student?.user?._id],
    queryFn:  () => getStudentEnrollments(student?.user?._id).then(r => r.data),
    enabled:  !!student?.user?._id,
  });

  const allClasses    = classData?.classes || [];
  const currentEnrolls = enrollData?.enrollments || [];
  const enrolledIds   = new Set(currentEnrolls.map(e => e.classId?._id).filter(Boolean));
  const available      = allClasses.filter(c => !enrolledIds.has(c._id));

  const toggleRemove = (classId) =>
    setToRemove(prev => { const next = new Set(prev); next.has(classId) ? next.delete(classId) : next.add(classId); return next; });
  const toggleAdd = (classId) =>
    setToAdd(prev => { const next = new Set(prev); next.has(classId) ? next.delete(classId) : next.add(classId); return next; });

  const hasChanges = toRemove.size > 0 || toAdd.size > 0;

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const removeIds = [...toRemove];
      const addIds    = [...toAdd];
      // Existing enroll/unenroll endpoints — same calls AssignClassModal already uses.
      await Promise.all([
        ...removeIds.map(classId => {
          const enrollment = currentEnrolls.find(e => e.classId?._id === classId);
          return enrollment ? unenroll(enrollment._id) : Promise.resolve();
        }),
        ...addIds.map(classId => enrollStudent({ studentId: student.user._id, classId })),
      ]);
      await studentService.resolveClassPlacementReview(student._id, { action: 'moved', addedClassIds: addIds, removedClassIds: removeIds });
    },
    onSuccess: () => {
      toast.success('Classes updated and review resolved');
      queryClient.invalidateQueries({ queryKey: ['student-enrollments-admin', student.user?._id] });
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student-full', student._id] });
      onResolved?.();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not update classes'),
  });

  useEscapeKey(onClose);
  const loading = loadingClasses || loadingEnrollments;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <div>
            <h2 className="font-bold text-secondary">Change Class</h2>
            <p className="text-xs text-subtle mt-0.5">{student.user?.name}</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && [1, 2, 3].map(i => <div key={i} className="h-14 bg-nav-hover animate-pulse rounded-xl" />)}

          {!loading && (
            <>
              <div>
                <p className="text-xs font-semibold text-muted mb-2">Currently enrolled</p>
                {currentEnrolls.length === 0 ? (
                  <p className="text-xs text-subtle py-2">Not currently enrolled in any class.</p>
                ) : (
                  <div className="space-y-2">
                    {currentEnrolls.map(e => {
                      const cls = e.classId;
                      if (!cls) return null;
                      const marked = toRemove.has(cls._id);
                      return (
                        <button key={e._id} type="button" onClick={() => toggleRemove(cls._id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                            marked ? 'border-red-400/40 bg-red-500/5' : 'border-divider hover:border-red-400/30'
                          }`}>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${marked ? 'text-muted line-through' : 'text-secondary'}`}>{cls.name}</p>
                            <p className="text-xs text-subtle">{cls.days?.map(d => DAYS_SHORT[d] || d).join(', ')}{cls.startTime && ` · ${fmtTime(cls.startTime)}`}</p>
                          </div>
                          <span className={`text-[11px] font-semibold flex-shrink-0 ${marked ? 'text-red-500' : 'text-subtle'}`}>
                            {marked ? 'Remove' : 'Keep'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold text-muted mb-2">Add a class</p>
                {available.length === 0 ? (
                  <p className="text-xs text-subtle py-2">No other classes available.</p>
                ) : (
                  <div className="space-y-2">
                    {available.map(cls => {
                      const checked = toAdd.has(cls._id);
                      return (
                        <button key={cls._id} type="button" onClick={() => toggleAdd(cls._id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                            checked ? 'border-secondary bg-nav-hover' : 'border-divider hover:border-secondary'
                          }`}>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'bg-secondary border-secondary' : 'border-divider'}`}>
                            {checked && <MdCheckCircle className="text-card text-xs" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-secondary truncate">{cls.name}</p>
                            <p className="text-xs text-subtle">{cls.days?.map(d => DAYS_SHORT[d] || d).join(', ')}{cls.startTime && ` · ${fmtTime(cls.startTime)}`}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t border-divider flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-divider rounded-xl text-sm text-muted hover:bg-nav-hover">Cancel</button>
          <button onClick={() => mutate()} disabled={isPending || !hasChanges || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className={`flex-1 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed`}>
            {isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Record Fee Payment Modal ─────────────────────────────────────────────────

function RecordFeePaymentModal({ student, onClose }) {
  const { isReadOnly } = useImpersonation();
  const queryClient  = useQueryClient();
  const userId       = student.user?._id;
  const userName     = student.user?.name || 'Student';
  const currentMonth = ktmThisMonth(); // Kathmandu month, not UTC
  const todayStr     = ktmToday();      // Kathmandu day, not UTC

  const [month,     setMonth]     = useState(currentMonth);
  const [amount,    setAmount]    = useState('');
  const [method,    setMethod]    = useState('cash');
  const [paidDate,  setPaidDate]  = useState(todayStr);
  const [reference, setReference] = useState('');

  const { data: structureData } = useQuery({
    queryKey:  ['fee-structure'],
    queryFn:   () => feeSvc.getFeeStructure().then(r => r.data.structure),
    staleTime: 300000,
  });

  const [amountPrefilled, setAmountPrefilled] = useState(false);
  if (structureData?.monthlyFee && !amountPrefilled) {
    setAmount(String(structureData.monthlyFee));
    setAmountPrefilled(true);
  }

  const isAlreadyPaid = student.feeStatus === 'paid' && month === currentMonth;

  const mutation = useMutation({
    mutationFn: () => feeSvc.recordPayment({
      studentId:            userId,
      month,
      amount:               Number(amount),
      method,
      paidDate,
      transactionReference: reference || undefined,
    }),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Payment recorded successfully');
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student-full', student._id] });
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to record payment'),
  });

  const canSubmit = !!userId && !!month && !!amount && Number(amount) > 0 && !!method;

  useEscapeKey(onClose);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] px-4" onClick={onClose}>
      <div className="bg-card rounded-3xl shadow-2xl p-6 max-w-sm w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-bold text-secondary text-base">Record Payment</h3>
            <p className="text-xs text-subtle mt-0.5">{userName}</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
        {isAlreadyPaid && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2">
            <MdWarning className="text-amber-500 text-base flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              This month's fee is already marked as <strong>Paid</strong>. Recording a new payment will update the existing record.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Month</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Amount (NPR)</label>
            <input type="number" min="0" step="1" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="e.g. 1500"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Payment Method</label>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { value: 'cash',    label: 'Cash'    },
                { value: 'bank',    label: 'Bank'    },
                { value: 'esewa',   label: 'eSewa'   },
                { value: 'khalti',  label: 'Khalti'  },
                { value: 'fonepay', label: 'Fonepay' },
                { value: 'other',   label: 'Other'   },
              ].map(opt => (
                <button key={opt.value} type="button" onClick={() => setMethod(opt.value)}
                  className={`py-2 rounded-xl text-xs font-medium border transition-colors
                    ${method === opt.value ? 'bg-secondary text-card border-secondary' : 'border-divider text-muted hover:border-secondary'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Payment Date</label>
            <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">
              Reference <span className="text-subtle font-normal">(optional)</span>
            </label>
            <input type="text" value={reference} onChange={e => setReference(e.target.value)}
              placeholder="Transaction ID, cheque no., etc."
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary" />
          </div>
        </div>

        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-muted hover:bg-nav-hover transition-colors">
            Cancel
          </button>
          <button onClick={() => mutation.mutate()} disabled={!canSubmit || mutation.isPending || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="flex-1 py-2.5 bg-secondary text-card rounded-full text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200">
            {mutation.isPending ? 'Recording…' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

function Students() {
  const v = useClubVocab();
  const { club: tenantClub } = useTenant();
  const showBelts = hasBelts(tenantClub?.clubType); // hide belt filter/column for non-belt clubs
  const showClasses = hasClasses(tenantClub?.clubType); // hide attendance columns/filter for open-floor gyms
  const isMembershipClub = tenantClub?.clubType !== 'taekwondo'; // gym/yoga/general → membership column + filter
  // Gyms run on membership plans, not monthly fee generation — hide the fee-status filter for them
  // (the Membership filter above is their equivalent). !hasClasses ≡ gym among Stage-1 types.
  const showFeeFilter = showClasses;
  // Visible table columns: Member + Status + Fee + Actions (always) + Belt? + Membership? + (Attendance, Last Attended)?
  const colCount = 4 + (showBelts ? 1 : 0) + (isMembershipClub ? 1 : 0) + (showClasses ? 2 : 0);
  document.title = `${v.members} — TrainAxis`;
  const { formatDate } = useCalendar();
  const { isReadOnly } = useImpersonation();
  const navigate = useNavigate();

  const [search,          setSearch]          = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [beltFilter,      setBeltFilter]      = useState('');
  const [statusFilter,    setStatusFilter]    = useState('');
  const [feeStatusFilter, setFeeStatusFilter] = useState('');
  const [attHealthFilter, setAttHealthFilter] = useState('');
  const [membershipFilter, setMembershipFilter] = useState('');
  const [reviewFilter,    setReviewFilter]     = useState(''); // Stage B — 'true' = needs class-placement review
  const [needsAssignmentFilter, setNeedsAssignmentFilter] = useState(''); // 'true' = public trial-join flagged (class was full at signup)
  const [needsBeltReviewFilter, setNeedsBeltReviewFilter] = useState(''); // 'true' = self-signup flagged (no belt question asked)
  const [needsGuardianPhoneFilter, setNeedsGuardianPhoneFilter] = useState(''); // 'true' = under-18, no valid guardian phone on file
  const [page,            setPage]            = useState(1);
  const [limit,           setLimit]           = useState(10);
  const [sortBy,          setSortBy]          = useState('createdAt');
  const [sortOrder,       setSortOrder]       = useState('desc');
  const [showArchived,    setShowArchived]    = useState(false);
  const [inviteOpen,      setInviteOpen]      = useState(false);
  const [addOpen,         setAddOpen]         = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [assignTarget,    setAssignTarget]    = useState(null);
  const [openActions,          setOpenActions]          = useState(null);
  const [dlOpen,               setDlOpen]               = useState(false);
  const [dlLoading,            setDlLoading]            = useState(false);
  const [recordPaymentTarget,  setRecordPaymentTarget]  = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchTimer = useRef(null);
  const queryClient = useQueryClient();

  // Club payment details for the WhatsApp renewal reminder — membership clubs only.
  const { data: paySettingsData } = useQuery({
    queryKey:  ['payment-settings'],
    queryFn:   () => feeSvc.getPaymentSettings().then(r => r.data),
    enabled:   isMembershipClub,
    staleTime: 5 * 60 * 1000,
  });
  const paymentSettings = paySettingsData?.paymentSettings || null;

  // Membership expiring within 7 days OR already expired — powers the WhatsApp reminder row action.
  const isExpiringOrExpired = (student) => {
    if (!student.membershipExpiresAt) return false;
    return Math.ceil((new Date(student.membershipExpiresAt).getTime() - Date.now()) / 86400000) <= 7;
  };

  // Opens WhatsApp with a pre-filled membership-renewal reminder (club name, plan, expiry, amount,
  // and payment details if configured). No automation — a wa.me link, mirroring the fee reminders.
  const remindMembershipWhatsApp = (student) => {
    const phone = student.user?.phone;
    if (!phone) { toast.error('No phone number on file'); return; }
    const clubName = tenantClub?.name || 'our club';
    const planName = student.membershipPlanName || 'membership';
    const expiry   = student.membershipExpiresAt ? fmtDate(student.membershipExpiresAt) : '';
    const amount   = student.membershipPlanPrice != null ? `NPR ${Number(student.membershipPlanPrice).toLocaleString()}` : null;
    const ps = paymentSettings || {};
    const payLines = [
      (ps.bankName || ps.bankAccountNumber) ? `Bank: ${[ps.bankName, ps.bankAccountNumber].filter(Boolean).join(' ')}${ps.bankAccountName ? ` (${ps.bankAccountName})` : ''}` : null,
      ps.esewaId   ? `eSewa: ${ps.esewaId}`     : null,
      ps.khaltiId  ? `Khalti: ${ps.khaltiId}`   : null,
      ps.fonepayId ? `Fonepay: ${ps.fonepayId}` : null,
    ].filter(Boolean);
    const msg = (
      `Namaste ${student.user?.name || ''},\n\n` +
      `This is a reminder that your ${planName} at ${clubName}${expiry ? ` expires on ${expiry}` : ' is expiring'}.\n` +
      (amount ? `Renewal: ${amount}.\n` : '') +
      (payLines.length ? `\nTo renew, you can pay via:\n${payLines.join('\n')}\n` : '') +
      (ps.paymentInstructions ? `\n${ps.paymentInstructions}\n` : '') +
      `\nThank you,\n${clubName}`
    );
    if (!openWhatsApp(phone, msg)) toast.error('No usable WhatsApp number on file');
  };

  const { data, isPending, error } = useQuery({
    queryKey: ['students', page, limit, sortBy, sortOrder, debouncedSearch, beltFilter, statusFilter, feeStatusFilter, attHealthFilter, membershipFilter, reviewFilter, needsAssignmentFilter, needsBeltReviewFilter, needsGuardianPhoneFilter],
    queryFn: () => {
      // Only send params that have a value — never send empty-string filters,
      // so no backend version can misinterpret them and exclude all records.
      const params = { page, limit, sortBy, sortOrder };
      if (debouncedSearch)  params.search               = debouncedSearch;
      if (beltFilter)       params.belt                 = beltFilter;
      if (statusFilter)     params.isActive              = statusFilter;
      if (feeStatusFilter)  params.feeStatus             = feeStatusFilter;
      if (attHealthFilter)  params.attendanceHealth      = attHealthFilter;
      if (membershipFilter) params.membership            = membershipFilter;
      if (reviewFilter)     params.classPlacementReview  = reviewFilter;
      if (needsAssignmentFilter) params.needsClassAssignment = needsAssignmentFilter;
      if (needsBeltReviewFilter) params.needsBeltReview = needsBeltReviewFilter;
      if (needsGuardianPhoneFilter) params.needsGuardianPhone = needsGuardianPhoneFilter;
      return studentService.getStudents(params).then(r => r.data);
    },
    placeholderData: keepPreviousData,
    enabled: !showArchived,
  });

  const { data: archivedData, isPending: archivedPending, error: archivedError } = useQuery({
    queryKey: ['students-archived', debouncedSearch],
    queryFn: () =>
      studentService.getStudents({ archived: true, limit: 100, search: debouncedSearch }).then(r => r.data),
    enabled: showArchived,
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => restoreStudent(id),
    onSuccess: () => {
      toast.success(`${v.member} restored successfully`);
      queryClient.invalidateQueries({ queryKey: ['students-archived'] });
      queryClient.invalidateQueries({ queryKey: ['students'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to restore'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => studentService.updateStudentStatus(id, isActive),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      toast.success(`${v.member} ${vars.isActive ? 'activated' : 'deactivated'}`);
    },
    onError: () => toast.error('Failed to update status'),
  });

  const handleSearchChange = (value) => {
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(value); setPage(1); }, 400);
  };

  // Pre-select the membership filter from ?membership= (dashboard card click), once on mount.
  useEffect(() => {
    const m = searchParams.get('membership');
    if (m && ['active', 'expiring', 'expired', 'none'].includes(m)) { setMembershipFilter(m); setPage(1); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    setSearch(''); setDebouncedSearch('');
    setBeltFilter(''); setStatusFilter('');
    setFeeStatusFilter(''); setAttHealthFilter('');
    setMembershipFilter(''); setReviewFilter('');
    setNeedsAssignmentFilter(''); setNeedsBeltReviewFilter(''); setNeedsGuardianPhoneFilter('');
    setPage(1);
  };

  const hasActiveFilters = search || beltFilter || statusFilter || feeStatusFilter || attHealthFilter || membershipFilter || reviewFilter || needsAssignmentFilter || needsBeltReviewFilter || needsGuardianPhoneFilter;

  // Robust parsing — tolerant of any response shape (handles deploy version skew)
  const activeStudents   = data?.students || data?.data || (Array.isArray(data) ? data : []);
  const activeTotal      = data?.pagination?.total ?? data?.total ?? activeStudents.length;
  const activePages      = data?.pagination?.totalPages ?? data?.pages ?? 1;
  const archivedStudents = archivedData?.students || archivedData?.data || (Array.isArray(archivedData) ? archivedData : []);

  // Deep-link: /admin/students?student=<studentProfileId> opens that student's detail drawer.
  // First match the loaded list; if the student isn't on the current page, fetch by id.
  // Clears the param afterward so a refresh doesn't reopen it.
  useEffect(() => {
    const targetId = searchParams.get('student');
    if (!targetId || isPending) return;

    const match = activeStudents.find(
      s => String(s._id) === targetId || String(s.user?._id) === targetId
    );
    if (match) {
      setSelectedStudent(match);
      setSearchParams({}, { replace: true });
      return;
    }

    // Not on this page (pagination/archived) — fetch by id.
    let cancelled = false;
    (async () => {
      try {
        const res = await studentService.getStudentById(targetId);
        const fetched = res.data?.student;
        if (cancelled) return;
        if (fetched) setSelectedStudent(fetched);
        else toast.error(`${v.member} not found`);
      } catch {
        if (!cancelled) toast.error(`${v.member} not found`);
      } finally {
        if (!cancelled) setSearchParams({}, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isPending, activeStudents]);

  // Sortable headers — same field flips direction, new field defaults to asc; resets page
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };
  const sortIcon = (field) => (sortBy !== field ? '↕' : sortOrder === 'asc' ? '↑' : '↓');

  // If an action (deactivate/archive) empties the current page, step back one page
  useEffect(() => {
    if (!isPending && !showArchived && activeTotal > 0 && activeStudents.length === 0 && page > 1) {
      setPage(p => Math.max(1, p - 1));
    }
  }, [isPending, showArchived, activeTotal, activeStudents.length, page]);

  // Club access blocked (subscription suspended/expired) — show a clear panel
  // instead of a misleading empty "0 students" list.
  const blockErr = error || archivedError;
  if (isClubSuspended(blockErr)) return <SuspendedAccessPanel />;
  if (isClubCancelled(blockErr)) return <SuspendedAccessPanel cancelled />;

  return (
    <div className="space-y-4">
      {/* ── Page header ── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-secondary">{v.members}</h1>
          <p className="text-xs text-muted mt-0.5">Add and manage {v.members.toLowerCase()} in your club.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Active / Archived tabs */}
          <div className="flex items-center bg-nav-hover rounded-full p-1">
            <button
              onClick={() => { setShowArchived(false); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${!showArchived ? 'bg-secondary text-card' : 'text-muted hover:text-secondary'}`}>
              Active
            </button>
            <button
              onClick={() => setShowArchived(true)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 flex items-center gap-1 ${showArchived ? 'bg-secondary text-card' : 'text-muted hover:text-secondary'}`}>
              <MdArchive className="text-sm" /> Archived
            </button>
          </div>

          {!showArchived && (
            <>
              {/* Download dropdown */}
              <div className="relative">
                <button onClick={() => setDlOpen(o => !o)} disabled={dlLoading}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-card text-secondary border border-divider text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-50">
                  <MdDownload className="text-base" />
                  {dlLoading ? 'Generating…' : 'Download'}
                </button>
                {dlOpen && (
                  <>
                  <div className="fixed inset-0 z-40" onClick={() => setDlOpen(false)} />
                  <div className="absolute right-0 mt-1 w-44 bg-card border border-gray-100 rounded-xl shadow-lg z-50 py-1"
                    onMouseLeave={() => setDlOpen(false)}>
                    <button onClick={() => { downloadStudentListReport({ format: 'pdf' }, setDlLoading); setDlOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-secondary hover:bg-nav-hover transition-colors">
                      <MdDownload className="text-primary" /> Download PDF
                    </button>
                    <button onClick={() => { downloadStudentListReport({ format: 'csv' }, setDlLoading); setDlOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-secondary hover:bg-nav-hover transition-colors">
                      <MdTableChart className="text-primary" /> Export CSV
                    </button>
                  </div>
                  </>
                )}
              </div>

              {TRIAL_ENABLED && (
                <Link to="/admin/trial-students?source=public_website"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-card text-secondary border border-divider text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200">
                  Website Requests
                </Link>
              )}

              <button onClick={() => setAddOpen(true)} disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-card text-secondary border border-divider text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
                <MdPersonAdd className="text-lg" /> Add {v.member}
              </button>

              <Button variant="inverted" onClick={() => setInviteOpen(true)} disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}>
                <MdEmail className="text-lg" /> Invite {v.member}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Filters ── */}
      {!showArchived && (
        <Card className="p-3">
          <div className="flex flex-wrap gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px]">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle text-lg pointer-events-none" />
              <input
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Search name, phone, email, ID…"
                className="w-full pl-9 pr-3 py-2 bg-card border border-divider rounded-full text-sm focus:outline-none focus:border-divider focus:ring-2 focus:ring-divider transition-colors"
              />
            </div>
            {/* Belt — hidden for club types without grading */}
            {showBelts && (
            <div className="relative">
            <select value={beltFilter} onChange={e => { setBeltFilter(e.target.value); setPage(1); }}
              className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm text-muted truncate focus:outline-none focus:border-divider focus:ring-2 focus:ring-divider">
              <option value="">All Belts</option>
              {BELT_ORDER.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
            </div>
            )}
            {/* Membership — gym/yoga/general only */}
            {isMembershipClub && (
            <div className="relative">
            <select value={membershipFilter} onChange={e => { setMembershipFilter(e.target.value); setPage(1); }}
              className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm text-muted truncate focus:outline-none focus:border-divider focus:ring-2 focus:ring-divider">
              <option value="">All Memberships</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring soon</option>
              <option value="expired">Expired</option>
              <option value="none">No plan</option>
            </select>
            <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
            </div>
            )}
            {/* Status */}
            <div className="relative">
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm text-muted truncate focus:outline-none focus:border-divider focus:ring-2 focus:ring-divider">
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
            </div>
            {/* Fee status — monthly-fee-based; hidden for gyms (they use the Membership filter) */}
            {showFeeFilter && (
            <div className="relative">
            <select value={feeStatusFilter} onChange={e => { setFeeStatusFilter(e.target.value); setPage(1); }}
              className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm text-muted truncate focus:outline-none focus:border-divider focus:ring-2 focus:ring-divider">
              <option value="">All Fees</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="no_record">No Record</option>
            </select>
            <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
            </div>
            )}
            {/* Attendance health — hidden for open-floor gyms */}
            {showClasses && (
            <div className="relative">
            <select value={attHealthFilter} onChange={e => { setAttHealthFilter(e.target.value); setPage(1); }}
              className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm text-muted truncate focus:outline-none focus:border-divider focus:ring-2 focus:ring-divider">
              <option value="">All Attendance</option>
              <option value="good">Good</option>
              <option value="low">Low</option>
              <option value="none">Not Attended</option>
            </select>
            <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
            </div>
            )}
            {/* Class-placement review (Stage B) — hidden for open-floor gyms */}
            {showClasses && (
              <button
                type="button"
                onClick={() => { setReviewFilter(reviewFilter === 'true' ? '' : 'true'); setPage(1); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                  reviewFilter === 'true'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-card text-muted border-divider hover:text-secondary'
                }`}
              >
                <MdWarning className="text-sm" /> Needs Review
              </button>
            )}
            {showClasses && (
              <button
                type="button"
                onClick={() => { setNeedsAssignmentFilter(needsAssignmentFilter === 'true' ? '' : 'true'); setPage(1); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                  needsAssignmentFilter === 'true'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-card text-muted border-divider hover:text-secondary'
                }`}
              >
                <MdWarning className="text-sm" /> Needs Class Assignment
              </button>
            )}
            {showBelts && (
              <button
                type="button"
                onClick={() => { setNeedsBeltReviewFilter(needsBeltReviewFilter === 'true' ? '' : 'true'); setPage(1); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                  needsBeltReviewFilter === 'true'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-card text-muted border-divider hover:text-secondary'
                }`}
              >
                <MdWarning className="text-sm" /> Needs Belt Review
              </button>
            )}
            <button
              type="button"
              onClick={() => { setNeedsGuardianPhoneFilter(needsGuardianPhoneFilter === 'true' ? '' : 'true'); setPage(1); }}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                needsGuardianPhoneFilter === 'true'
                  ? 'bg-primary text-white border-primary'
                  : 'bg-card text-muted border-divider hover:text-secondary'
              }`}
            >
              <MdWarning className="text-sm" /> No Guardian Phone
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-xs text-subtle hover:text-secondary bg-card border border-divider rounded-full hover:bg-nav-hover transition-colors">
                <MdClose className="text-sm" /> Clear
              </button>
            )}
          </div>
        </Card>
      )}

      {/* ── Archived search ── */}
      {showArchived && (
        <Card className="p-3">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle text-lg pointer-events-none" />
            <input value={search} onChange={e => handleSearchChange(e.target.value)}
              placeholder={`Search archived ${v.members.toLowerCase()}…`}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary transition-colors" />
          </div>
        </Card>
      )}

      {/* ── Archived students table ── */}
      {showArchived && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-amber-50 border-b border-amber-100 text-xs text-amber-700 font-semibold uppercase tracking-wide">
                  <th className="text-left px-5 py-3">{v.member}</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">ID</th>
                  {showBelts && <th className="text-left px-4 py-3">Belt</th>}
                  <th className="text-left px-4 py-3">Archived</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {archivedPending ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={6} />)
                ) : archivedStudents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16 text-subtle">
                      <MdArchive className="text-4xl mx-auto mb-2 text-subtle" />
                      <p className="text-sm">No archived {v.members.toLowerCase()}</p>
                    </td>
                  </tr>
                ) : archivedStudents.map(student => (
                  <tr key={student._id} className="border-b border-gray-50 last:border-0 hover:bg-amber-50/40 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar name={student.user?.name} photoUrl={student.profilePhoto} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-secondary leading-tight truncate">{student.user?.name}</p>
                          <p className="text-xs text-subtle">{student.discipline}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-subtle max-w-[180px] truncate">
                      {student.user?.email?.includes('@noemail.club') ? '—' : student.user?.email}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-xs bg-gray-100 text-muted px-2 py-0.5 rounded-md">{getDisplayTxId(student)}</span>
                    </td>
                    {showBelts && (
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getBeltStyle(student.currentGrade)}`}>
                        {student.currentGrade}
                      </span>
                    </td>
                    )}
                    <td className="px-4 py-3.5 text-xs text-subtle whitespace-nowrap">
                      {student.deletedAt ? <DualDate date={student.deletedAt} /> : '—'}
                    </td>
                    <td className="px-4 py-3.5">
                      <button
                        title={isReadOnly ? 'Disabled: read-only support mode' : 'Restore Student'}
                        disabled={restoreMutation.isPending || isReadOnly}
                        onClick={() => restoreMutation.mutate(student._id)}
                        className="flex items-center gap-1 text-xs font-semibold text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                        <MdRestore className="text-base" /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Active students table ── */}
      {!showArchived && (
        <Card className="p-0 overflow-hidden">
          {/* Summary bar */}
          {!isPending && (
            <div className="px-5 py-2.5 border-b border-gray-50 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-subtle">
                {activeTotal} {activeTotal !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()}
                {hasActiveFilters ? ' matching filters' : ' registered'}
              </p>
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="text-xs text-subtle">Rows per page</span>
                <div className="relative">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="appearance-none pl-4 pr-9 py-1 text-xs bg-card border border-gray-200 rounded-lg truncate focus:outline-none focus:border-primary"
                >
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <MdExpandMore className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs text-subtle font-semibold uppercase tracking-wide">
                  <th className="text-left px-5 py-3">
                    <button onClick={() => toggleSort('fullName')} className="inline-flex items-center gap-1 uppercase hover:text-primary transition-colors">
                      {v.member} <span className="text-[10px]">{sortIcon('fullName')}</span>
                    </button>
                  </th>
                  {showBelts && (
                  <th className="text-left px-4 py-3">
                    <button onClick={() => toggleSort('currentBelt')} className="inline-flex items-center gap-1 uppercase hover:text-primary transition-colors">
                      Belt <span className="text-[10px]">{sortIcon('currentBelt')}</span>
                    </button>
                  </th>
                  )}
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Fee</th>
                  {isMembershipClub && <th className="text-left px-4 py-3">Membership</th>}
                  {showClasses && <th className="text-left px-4 py-3 hidden md:table-cell">Attendance</th>}
                  {showClasses && <th className="text-left px-4 py-3 hidden lg:table-cell">Last Attended</th>}
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isPending ? (
                  Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={colCount} />)
                ) : activeStudents.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="text-center py-16 text-subtle">
                      <MdPersonAdd className="text-5xl mx-auto mb-3 text-subtle" />
                      <p className="text-sm font-semibold text-muted mb-1">
                        {hasActiveFilters ? `No ${v.members.toLowerCase()} match these filters` : `No ${v.members.toLowerCase()} yet`}
                      </p>
                      <p className="text-xs text-subtle mb-4">
                        {hasActiveFilters
                          ? 'Try adjusting your search or filters.'
                          : TRIAL_ENABLED
                            ? `Add ${v.members.toLowerCase()} directly or convert from Website Requests.`
                            : `Add ${v.members.toLowerCase()} directly.`}
                      </p>
                      {hasActiveFilters ? (
                        <button onClick={clearFilters} className="text-primary text-xs font-semibold hover:underline">
                          Clear all filters
                        </button>
                      ) : (
                        <div className="flex items-center justify-center gap-3 mb-2">
                          <button onClick={() => setInviteOpen(true)}
                            className="flex items-center gap-1.5 text-primary text-xs font-semibold hover:underline">
                            <MdPersonAdd /> Invite {v.member}
                          </button>
                          {TRIAL_ENABLED && (
                            <Link to="/admin/trial-students?source=public_website"
                              className="flex items-center gap-1.5 text-subtle text-xs hover:underline">
                              View Website Requests
                            </Link>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ) : activeStudents.map(student => (
                  <tr key={student._id}
                    onClick={() => setSelectedStudent(student)}
                    className="border-b border-divider last:border-0 hover:bg-nav-hover transition-colors cursor-pointer">
                    {/* Student cell */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar name={student.user?.name} photoUrl={student.profilePhoto} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="text-sm font-semibold text-secondary leading-tight truncate">{student.user?.name}</p>
                            {student.user?.isRecordOnly ? (
                              <MdPersonOff title="Record only — no platform access" className="text-subtle text-sm flex-shrink-0" />
                            ) : (student.user?.email && !student.user.email.includes('@noemail.club')) ? (
                              <MdVerifiedUser title="Has platform access" className="text-green-600 text-sm flex-shrink-0" />
                            ) : null}
                            {showClasses && student.classPlacementReviewRequired && (
                              <MdWarning title="Age group changed — class review required" className="text-amber-500 text-sm flex-shrink-0" />
                            )}
                            {showClasses && student.classAssignmentPending?.classId && (
                              <MdWarning title={`Needs class assignment — wanted ${student.classAssignmentPending.className}, class was full at signup`} className="text-amber-500 text-sm flex-shrink-0" />
                            )}
                            {student.needsGuardianPhone && (
                              <MdWarning title="Under 18, no parent/guardian phone on file" className="text-amber-500 text-sm flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const id = getDisplayTxId(student);
                                if (id !== 'ID pending') {
                                  navigator.clipboard.writeText(id).catch(() => {});
                                  toast.success('TrainAxis ID copied!', { duration: 1200 });
                                }
                              }}
                              className="font-mono text-[10px] bg-gray-100 text-muted px-1.5 py-0.5 rounded hover:bg-nav-hover transition-colors">
                              {getDisplayTxId(student)}
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Belt */}
                    {showBelts && (
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getBeltStyle(student.currentGrade)}`}>
                        {student.currentGrade}
                      </span>
                      {student.beltNeedsReview && (
                        <MdWarning title="Needs belt review — signed up with no belt question asked" className="inline-block ml-1.5 text-amber-500 text-sm" />
                      )}
                    </td>
                    )}
                    {/* Status */}
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${student.user?.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {student.user?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {/* Fee status */}
                    <td className="px-4 py-3.5">
                      <FeeBadge status={student.feeStatus} source={student.feeSource} />
                    </td>
                    {/* Membership — gym/yoga/general only */}
                    {isMembershipClub && (
                      <td className="px-4 py-3.5">
                        <MembershipBadge planName={student.membershipPlanName} expiresAt={student.membershipExpiresAt} />
                      </td>
                    )}
                    {/* Attendance health */}
                    {showClasses && (
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <AttBadge health={student.attendanceHealth} />
                    </td>
                    )}
                    {/* Last attended */}
                    {showClasses && (
                    <td className="px-4 py-3.5 text-xs text-subtle whitespace-nowrap hidden lg:table-cell">
                      {student.lastAttendedAt ? fmtDate(student.lastAttendedAt) : '—'}
                    </td>
                    )}
                    {/* Actions */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        {/* View Profile */}
                        <button
                          title="View Profile"
                          onClick={(e) => { e.stopPropagation(); setSelectedStudent(student); }}
                          className="w-10 h-10 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover hover:text-secondary transition-colors">
                          <MdRemoveRedEye className="text-lg" />
                        </button>

                        {/* More actions dropdown */}
                        <div className="relative">
                          <button
                            title="More actions"
                            onClick={e => { e.stopPropagation(); setOpenActions(openActions === student._id ? null : student._id); }}
                            className="w-10 h-10 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors">
                            <MdMoreVert className="text-lg" />
                          </button>
                          {openActions === student._id && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setOpenActions(null)} />
                              <div className="absolute right-0 top-full mt-1 bg-card border border-gray-100 rounded-xl shadow-lg z-50 min-w-[168px] py-1"
                                onClick={e => e.stopPropagation()}>
                                {/* Membership renewal nudge — expiring/expired members with a phone (no automation) */}
                                {isMembershipClub && isExpiringOrExpired(student) && student.user?.phone && (
                                  <button
                                    onClick={() => { remindMembershipWhatsApp(student); setOpenActions(null); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-nav-hover">
                                    <FaWhatsapp className="text-green-600" /> Remind on WhatsApp
                                  </button>
                                )}
                                <button
                                  onClick={() => { setAssignTarget(student); setOpenActions(null); }}
                                  disabled={isReadOnly}
                                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-nav-hover disabled:opacity-50 disabled:cursor-not-allowed">
                                  <MdAdd className="text-blue-500" /> Assign to Class
                                </button>
                                <button
                                  onClick={() => { setRecordPaymentTarget(student); setOpenActions(null); }}
                                  disabled={isReadOnly}
                                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-nav-hover disabled:opacity-50 disabled:cursor-not-allowed">
                                  <MdPayments className="text-green-500" /> Record Payment
                                </button>
                                <button
                                  onClick={() => { downloadStudentProfile(student._id); setOpenActions(null); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-nav-hover">
                                  <MdDownload className="text-primary" /> Download Report
                                </button>
                                <div className="border-t border-gray-100 my-1" />
                                <button
                                  onClick={() => { statusMutation.mutate({ id: student._id, isActive: !student.user?.isActive }); setOpenActions(null); }}
                                  disabled={statusMutation.isPending || isReadOnly}
                                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-nav-hover disabled:opacity-40 disabled:cursor-not-allowed">
                                  {student.user?.isActive
                                    ? <><MdBlock className="text-red-400" /> Deactivate</>
                                    : <><MdCheckCircle className="text-green-500" /> Activate</>}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {activeTotal > limit && (
            <Pagination page={page} pages={activePages} total={activeTotal} limit={limit} onChange={setPage} />
          )}
        </Card>
      )}

      {/* Student Detail Drawer */}
      {selectedStudent && (
        <StudentDrawer
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          onAssignClass={setAssignTarget}
          onRecordPayment={setRecordPaymentTarget}
        />
      )}

      {/* Assign to Class Modal */}
      {assignTarget && (
        <AssignClassModal student={assignTarget} onClose={() => setAssignTarget(null)} />
      )}

      {recordPaymentTarget && (
        <RecordFeePaymentModal
          student={recordPaymentTarget}
          onClose={() => setRecordPaymentTarget(null)}
        />
      )}

      <PendingInvitationsPanel role="student" />

      {inviteOpen && (
        <InviteModal lockedRole="student" onClose={() => setInviteOpen(false)} />
      )}

      {addOpen && (
        <AddStudentModal
          isTrainer={false}
          onClose={() => setAddOpen(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['students'] })}
        />
      )}
    </div>
  );
}

export default Students;
